import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EventTracker, type EventTrackerDeps } from '../events/event-tracker';
import { PerformanceTracker, type PerformanceTrackerDeps } from '../trackers/performance-tracker';
import { SessionTracker, type SessionTrackerDeps } from '../trackers/session-tracker';
import type { AnalyticsConfig, AnalyticsEvent } from '../types/analytics';

export interface AnalyticsContextValue {
  tracker: EventTracker;
  performance: PerformanceTracker;
  session: SessionTracker;
  track: (event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId' | 'userId'>) => void;
  trackPageView: (path: string) => void;
  trackGameEvent: (gameId: string, eventName: string, data?: object) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export interface AnalyticsProviderProps {
  children: ReactNode;
  config?: Partial<AnalyticsConfig>;
  trackerDeps?: EventTrackerDeps;
  performanceDeps?: PerformanceTrackerDeps;
  sessionDeps?: SessionTrackerDeps;
}

export function AnalyticsProvider({
  children,
  config,
  trackerDeps,
  performanceDeps,
  sessionDeps,
}: AnalyticsProviderProps) {
  const [session] = useState(() => new SessionTracker(sessionDeps));
  const [tracker] = useState(() => new EventTracker(session.getSessionId(), config, trackerDeps));
  const [performance] = useState(() => new PerformanceTracker(performanceDeps));

  useEffect(() => {
    session.startHeartbeat(30_000, () => {
      tracker.track({ name: 'heartbeat', category: 'system', properties: {} });
    });
    return () => {
      tracker.destroy();
      performance.destroy();
      session.destroy();
    };
  }, [tracker, performance, session]);

  const track = useCallback(
    (event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId' | 'userId'>) => tracker.track(event),
    [tracker],
  );

  const trackPageView = useCallback(
    (path: string) => tracker.trackPageView(path),
    [tracker],
  );

  const trackGameEvent = useCallback(
    (gameId: string, eventName: string, data?: object) => tracker.trackGameEvent(gameId, eventName, data),
    [tracker],
  );

  const value = useMemo<AnalyticsContextValue>(
    () => ({ tracker, performance, session, track, trackPageView, trackGameEvent }),
    [tracker, performance, session, track, trackPageView, trackGameEvent],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnalytics(): Pick<AnalyticsContextValue, 'track' | 'trackPageView' | 'trackGameEvent'> {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalytics must be used within an AnalyticsProvider');
  return { track: ctx.track, trackPageView: ctx.trackPageView, trackGameEvent: ctx.trackGameEvent };
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePerformance(): PerformanceTracker {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('usePerformance must be used within an AnalyticsProvider');
  return ctx.performance;
}
